/**
 * Shared poll-budget rule for the live feeding refresh, used by both
 * `useFeedingDashboard` and `useGetFeedConversionRate`. Kept as one function so the
 * two hooks cannot drift apart on the tiers or the boundary values.
 *
 * Three tiers rather than a fixed budget: 8 polls every 15 s (unchanged from the
 * original cap, and enough for providers that finish in under a minute), then 8 more
 * every 60 s. The slow tier exists because a background provider fetch routinely
 * outlives the original 2-minute budget (Arnalax up to 6 minutes, Aquagroup up to
 * 3.5, Clarify one 60 s call per mapped signal, plus queueing behind a 4-slot gate),
 * so the fast-only cap stopped polling while a real fetch was still in flight.
 *
 * Past those 16 budgeted polls (about 10 minutes, matching
 * `FEEDING_LIVE_REFRESH_COOLDOWN_MINUTES`), polling does not stop: it drops to once
 * every `REFRESH_POLL_TRAILING_MS`, for as long as the response keeps reporting
 * `refreshing`. This third tier is what guarantees a final refetch actually lands.
 * The backend now bounds `refreshing` itself to
 * `FEEDING_LIVE_REFRESH_ATTEMPT_TIMEOUT_MINUTES`
 * (`FeedingRefreshService.runAttempt`'s overall attempt deadline); a poller only
 * learns of the flip on its next poll, so in practice this tier sees it within
 * about that timeout plus one more poll interval, never indefinitely. No count cap
 * is needed here the way there was for the first two tiers, because the thing that
 * used to make an uncapped poll risky (a `fetchCage` call with no deadline of its
 * own) no longer applies.
 */

/** Fast tier: how often to re-poll for the first `REFRESH_POLL_FAST_COUNT` polls. */
export const REFRESH_POLL_FAST_MS = 15_000;
/** Number of fast-tier polls before switching to the slow tier. */
export const REFRESH_POLL_FAST_COUNT = 8;
/** Slow tier: how often to re-poll from `REFRESH_POLL_FAST_COUNT` up to `REFRESH_POLL_TOTAL_COUNT`. */
export const REFRESH_POLL_SLOW_MS = 60_000;
/** Number of budgeted (fast + slow tier) polls before switching to the trailing tier. */
export const REFRESH_POLL_TOTAL_COUNT = 16;
/** Trailing tier: how often to re-poll past `REFRESH_POLL_TOTAL_COUNT`, for as long as `refreshing` stays true. */
export const REFRESH_POLL_TRAILING_MS = 300_000;

/**
 * Decides the next poll delay for a live feeding refresh, or `false` to stop.
 *
 * `settledFetches` is `dataUpdateCount + errorUpdateCount` from the react-query
 * `Query` object: react-query's own count of settled fetches (successful and
 * failed) for this query identity. Counting failures, not only successes, matters
 * because a failing provider still leaves `refreshing: true` in the last good
 * response: with this app's default retry policy a cap that only counted successes
 * would see the count stuck forever and re-arm the fast interval indefinitely
 * against a provider that is down. The sum is 1 right after the initial load, so
 * subtracting 1 below counts polls only, not the initial fetch.
 *
 * Never returns `false` while `refreshing` is true: past the budgeted tiers this
 * falls through to the trailing interval instead of stopping, so a poll that
 * observes `refreshing: false` is always the one that ends polling, and it always
 * carries the data that made it false.
 */
export function refreshPollInterval(refreshing: boolean, settledFetches: number): number | false {
    if (!refreshing) return false;

    const pollsSoFar = settledFetches - 1;
    if (pollsSoFar < REFRESH_POLL_FAST_COUNT) return REFRESH_POLL_FAST_MS;
    if (pollsSoFar < REFRESH_POLL_TOTAL_COUNT) return REFRESH_POLL_SLOW_MS;
    return REFRESH_POLL_TRAILING_MS;
}

/** True once `settledFetches` has passed the fast+slow budget: the point the "taking longer" hint appears and trailing polling begins. */
export function isRefreshPollingSlow(settledFetches: number): boolean {
    return settledFetches - 1 >= REFRESH_POLL_TOTAL_COUNT;
}
